import { useAuth } from '@clerk/clerk-react'
import { useCallback, useEffect, useState } from 'react'

import { createFetcher } from '@/services/api'
import type { FeaturebaseChangelogConfig } from '../types/featurebase'
import { isE2ETest } from '../utils/testUtils'

const FEATUREBASE_SDK_URL = 'https://do.featurebase.app/js/sdk.js'
const FEATUREBASE_SDK_ID = 'featurebase-sdk'

// Module-level state. The widget SDK is a singleton — we register its
// unread-count callback exactly once per browser session, then fan out
// updates to every hook instance that's currently mounted. Header remounts
// on every route change, so without this fan-out the unread badge would
// only ever reflect the first mount's snapshot.
let sdkInitStarted = false
let sdkInitialized = false
let currentUnreadCount = 0
const unreadSubscribers = new Set<(count: number) => void>()

function notifyUnread(count: number) {
  currentUnreadCount = count
  for (const subscriber of unreadSubscribers) subscriber(count)
}

interface FeaturebaseUserData {
  email: string
  name: string
  userId: string
}

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(FEATUREBASE_SDK_ID)) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.id = FEATUREBASE_SDK_ID
    script.src = FEATUREBASE_SDK_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('[Featurebase] Failed to load SDK script'))
    document.head.appendChild(script)
  })
}

function ensureQueue() {
  if (typeof window.Featurebase !== 'function') {
    const fb = (...args: unknown[]) => {
      fb.q = fb.q || []
      fb.q.push(args)
    }
    fb.q = [] as unknown[]
    window.Featurebase = fb as unknown as typeof window.Featurebase
  }
}

/**
 * Lazy-load the Featurebase changelog widget (Card view).
 *
 * Auto-pop card: when initialized, Featurebase shows a card automatically if
 * there are unseen updates (`changelogCard: { enabled: true }`).
 * Manual open: call `open()` to trigger the changelog popup on demand.
 * Unread count: surfaced as `unreadCount` for badging UI. Each mount seeds
 * from the module-level cache synchronously and subscribes for updates, so
 * the badge stays live across route-driven Header remounts.
 */
export function useFeaturebaseChangelogWidget(userData?: FeaturebaseUserData) {
  const { getToken } = useAuth()
  // Seed synchronously from the module-level cache so a remounted Header
  // shows the last known count immediately (no flicker to zero).
  const [unreadCount, setUnreadCount] = useState<number>(() => currentUnreadCount)
  const [isInitialized, setIsInitialized] = useState<boolean>(() => sdkInitialized)

  // Subscribe to unread updates for the lifetime of this mount.
  useEffect(() => {
    unreadSubscribers.add(setUnreadCount)
    // Re-seed on subscribe in case the count changed between render and effect.
    setUnreadCount(currentUnreadCount)
    return () => {
      unreadSubscribers.delete(setUnreadCount)
    }
  }, [])

  // Initialize the widget exactly once per browser session.
  useEffect(() => {
    if (isE2ETest() || sdkInitStarted || !userData?.email) {
      // If the widget already initialized in a prior mount, mirror that flag.
      if (sdkInitialized) setIsInitialized(true)
      return
    }

    const organization = import.meta.env.VITE_FEATUREBASE_ORG
    if (!organization) {
      console.error(
        '[Featurebase] Organization is not set. Please define VITE_FEATUREBASE_ORG in your environment variables.'
      )
      return
    }

    sdkInitStarted = true

    ;(async () => {
      try {
        const fetcher = createFetcher(getToken)
        const nameParam = encodeURIComponent(userData.name)
        const { token: featurebaseJwt } = await fetcher<{ token: string }>(
          `/integrations/featurebase-changelog-token?name=${nameParam}`
        )

        ensureQueue()

        // Fire-and-forget identify — some SDK versions never invoke the callback,
        // which would hang an awaited Promise indefinitely.
        window.Featurebase!('identify', { organization, featurebaseJwt })

        await loadScript()

        const config: FeaturebaseChangelogConfig = {
          organization,
          theme: 'dark',
          locale: 'en',
          featurebaseJwt,
          popup: {
            enabled: true,
            usersName: userData.name,
            autoOpenForNewUpdates: true,
          },
          changelogCard: {
            enabled: true,
            layout: { position: 'bottom-right' },
          },
        }

        // Must be `init_changelog_widget` (new schema). `initialize_changelog_widget`
        // uses a legacy schema that silently strips the popup/changelogCard blocks.
        //
        // Featurebase's changelog widget does NOT emit a `widgetReady` action
        // (that's a feedback-widget signal). The first callback we ever get is
        // typically `unreadChangelogsCountChanged`, so we treat any successful
        // callback as "the widget has finished loading and is ready for open()".
        window.Featurebase!('init_changelog_widget', config, (err, result) => {
          if (err) {
            console.error('[Featurebase] Changelog widget error:', err)
            return
          }
          if (!sdkInitialized) {
            sdkInitialized = true
            setIsInitialized(true)
          }
          if (result?.action === 'unreadChangelogsCountChanged' && typeof result.unreadCount === 'number') {
            notifyUnread(result.unreadCount)
          }
        })
      } catch (error) {
        console.error('[Featurebase] Failed to initialize changelog widget:', error)
        sdkInitStarted = false
      }
    })()
  }, [userData?.email, userData?.name, getToken])

  const open = useCallback(() => {
    if (!sdkInitialized || typeof window.Featurebase !== 'function') {
      console.warn('[Featurebase] Changelog widget not ready')
      return
    }
    window.Featurebase('manually_open_changelog_popup')
  }, [])

  return { open, isInitialized, unreadCount }
}
